<template>
  <div class="peding">
    <vs-table :data="requests" :loading="loading">
        <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between m-2">
          <div class="flex flex-wrap gap-3 items-end">
          <vs-button icon-pack="feather" type="border" icon="icon-plus" v-if="canCreate" @click.prevent="openNew">Novo Agendamento</vs-button>
          </div>
        <div class="flex flex-wrap-reverse items-center">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bgcursor-pointer flex items-center justify-between font-medium">
              <span class="mr-2">
                {{ pageStart }} - {{ pageEnd }} de {{ totalItems }}
              </span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="changeItemsPerPage(4)">4</vs-dropdown-item>
              <vs-dropdown-item @click="changeItemsPerPage(10)">10</vs-dropdown-item>
              <vs-dropdown-item @click="changeItemsPerPage(15)">15</vs-dropdown-item>
              <vs-dropdown-item @click="changeItemsPerPage(20)">20</vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
      </div>
      <template slot="thead">
        <vs-th>ID</vs-th>
        <vs-th>Ação</vs-th>
        <vs-th>Alvo</vs-th>
        <vs-th>Status</vs-th>
        <vs-th>Agendado</vs-th>
        <vs-th>Ações</vs-th>
      </template>

      <template slot-scope="{data}">
        <vs-tr :key="item.id" v-for="item in data">
          <vs-td class="product-name font-medium truncate">{{ item.id }}</vs-td>
          <vs-td class="product-name font-medium truncate">{{ actionLabel(item.action_type) }}</vs-td>
          <vs-td class="product-name font-medium truncate">{{ tenantLabel(item.target_id) }}</vs-td>
          <vs-td>
            <div class="product-name font-medium truncate inline-flex">
              <vs-chip :color="approvalStatusColor(item.status)">
                {{ approvalStatusLabel(item.status) }}
              </vs-chip>
            </div>
          </vs-td>
          <vs-td class="product-name font-medium truncate">{{ formatDate(item.scheduled_at).full }}</vs-td>
          <vs-td class="product-name font-medium truncate">
            <div class="inline-flex gap-2">
              <vs-button size="small" type="border" icon-pack="feather" icon="icon-eye" @click="goDetail(item)"/>
              <vs-button size="small" type="border" icon-pack="feather" icon="icon-edit" color="primary" v-if="canEdit(item)" @click="edit(item)"/>
              <vs-button size="small" type="border" icon-pack="feather" icon="icon-trash" color="danger" v-if="canCancel(item)" @click="cancel(item)"/>
            </div>
          </vs-td>
        </vs-tr>
      </template>
      <template slot="empty">
        <div class="empty-state">
          <div class="title">Nenhuma solicitação encontrada</div>
          <div class="subtitle">Clique em “Novo Agendamento” para programar uma desativação.</div>
        </div>
      </template>
    </vs-table>

    <div class="mt-3 mr-4 flex justify-end" v-if="totalPages > 1">
      <vs-pagination :total="totalPages" v-model="page" @change="handleChangePage" max="7" />
    </div>

    <request-form-modal
      :active.sync="showForm"
      :request="editingRequest"
      :allow-bulk-import="true"
      @saved="onSaved"
    />

    <vs-popup title="Cancelar solicitação" :active.sync="cancelModal.active" class="cancel-popup">
      <div v-if="cancelModal.item">
        <p class="mb-2 text-sm text-grey">ID #{{ cancelModal.item.id }} · {{ actionLabel(cancelModal.item.action_type) }}</p>
        <vs-textarea
          v-model="cancelModal.comment"
          label="Motivo do cancelamento"
          placeholder="Escreva o motivo do cancelamento"
          class="mb-4"
        />
        <div class="flex justify-end gap-2">
          <vs-button type="border" @click="closeCancel" :disabled="cancelModal.loading">Voltar</vs-button>
          <vs-button color="danger" @click="confirmCancel" :loading="cancelModal.loading">Cancelar solicitação</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import tenantsRequest from '@/http/requests/tenants'
import RequestFormModal from '@/components/approvals/RequestFormModal.vue'
import { formatDate, hasPermissionBackOffice, tenantLabel as tenantLabelUtil, filterTenantsByHost, formatTenantLabel, approvalStatusLabel, approvalStatusColor } from '@/utils'

export default {
  name: 'MyRequests',
  components: {
    RequestFormModal
  },
  data() {
    return {
      showForm: false,
      editingRequest: null,
      tenantsMap: {},
      tenantsLoading: false,
      cancelModal: {
        active: false,
        item: null,
        comment: '',
        loading: false
      },
      itemsPerPage: 4,
      page: 1
    }
  },
  computed: {
    ...mapGetters('approvals', ['requests', 'requestsPagination', 'approvalsLoading']),
    totalItems() {
      return this.requestsPagination && this.requestsPagination.totalItems ? this.requestsPagination.totalItems : 0;
    },
    totalPages() {
      return this.requestsPagination && this.requestsPagination.totalPages ? this.requestsPagination.totalPages : 1;
    },
    pageStart() {
      if (!this.totalItems) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    pageEnd() {
      if (!this.totalItems) return 0;
      const end = this.pageStart + this.requests.length - 1;
      return end > this.totalItems ? this.totalItems : end;
    },
    loading() {
      return this.approvalsLoading;
    },
    canCreate() {
      return hasPermissionBackOffice('requests');
    },
    canList() {
      return hasPermissionBackOffice('requests');
    },
    canUpdate() {
      return hasPermissionBackOffice('requests-update');
    },
    canRemove() {
      return hasPermissionBackOffice('requests-remove');
    }
  },
  methods: {
    formatDate,
    approvalStatusLabel,
    approvalStatusColor,
    actionLabel(actionType) {
      const map = {
        tenant_deactivation: 'Desativação de tenant'
      };
      return map[actionType] || actionType;
    },
    tenantLabel(id) {
      return tenantLabelUtil(this.tenantsMap, id);
    },
    openNew() {
      this.editingRequest = null;
      this.showForm = true;
    },
    onSaved() {
      this.loadRequests();
    },
    async loadRequests() {
      if (!this.canList) {
        this.$vs.notify({ color: 'warning', title: 'Permissão', text: 'Você não tem permissão para visualizar.' });
        return;
      }
      const response = await this.$store.dispatch('approvals/fetchRequests', {
        page: this.page,
        limit: this.itemsPerPage
      });

      if (response && response.total_pages && this.page > response.total_pages) {
        this.page = response.total_pages;
        await this.loadRequests();
      }
    },
    async loadTenants() {
      try {
        this.tenantsLoading = true;
        const response = await tenantsRequest.fetchPaginatedTenants(
          localStorage.getItem('accessToken'),
          true,
          1,
          500,
          '',
          'fqdn',
          'ASC'
        );
        const data = response.data || {};
        const items = filterTenantsByHost(data.items || data.data || data || []);
        const map = {};
        items.forEach(item => {
          map[item.id] = formatTenantLabel(item);
        });
        this.tenantsMap = map;
      } catch (error) {
        console.error(error);
      } finally {
        this.tenantsLoading = false;
      }
    },
    goDetail(item) {
      this.$store.dispatch('approvals/setCurrentRequest', item);
      this.$router.push(`/approvals/${item.id}`);
    },
    canEdit(item) {
      return this.canUpdate && this.isOpenSchedule(item);
    },
    canCancel(item) {
      return this.canRemove && this.isOpenSchedule(item);
    },
    isOpenSchedule(item) {
      if (!item || !['pending', 'approved'].includes(item.status)) {
        return false;
      }

      const scheduledAction = item.scheduled_action;
      if (!scheduledAction) {
        return true;
      }

      return !scheduledAction.is_canceled && !scheduledAction.executed_at;
    },
    edit(item) {
      this.editingRequest = item;
      this.showForm = true;
    },
    cancel(item) {
      this.cancelModal.item = item;
      this.cancelModal.comment = '';
      this.cancelModal.active = true;
    },
    async confirmCancel() {
      if (!this.cancelModal.item) return;
      const comment = (this.cancelModal.comment || '').trim();
      if (!comment) {
        this.$vs.notify({ color: 'warning', title: 'Campos obrigatórios', text: 'Informe o motivo do cancelamento antes de continuar.' });
        return;
      }
      this.cancelModal.loading = true;
      const id = this.cancelModal.item.id;
      try {
        await this.$store.dispatch('approvals/cancelRequest', { id, comment });
        await this.loadRequests();
        this.$vs.notify({ color: 'success', title: 'Solicitação', text: 'Solicitação cancelada.' });
      } catch (error) {
        this.$vs.notify({ color: 'danger', title: 'Erro', text: 'Não foi possível cancelar.' });
      } finally {
        this.cancelModal.loading = false;
        this.cancelModal.active = false;
        this.cancelModal.item = null;
        this.cancelModal.comment = '';
      }
    },
    closeCancel() {
      this.cancelModal.active = false;
      this.cancelModal.item = null;
      this.cancelModal.comment = '';
    },
    async handleChangePage(page) {
      this.page = page;
      await this.loadRequests();
    },
    async changeItemsPerPage(limit) {
      if (this.itemsPerPage === limit) {
        return;
      }

      this.itemsPerPage = limit;
      this.page = 1;
      await this.loadRequests();
    }
  },
  async created() {
    await this.loadTenants();
    await this.loadRequests();
  }
}
</script>

<style lang="scss">
.peding{
  .vs-con-table {

    .product-name {
      max-width: 23rem;
    }

    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      margin-left: 1.5rem;
      margin-right: 1.5rem;

      > span {
        display: flex;
        flex-grow: 1;
      }

      .vs-table--search {
        padding-top: 0;

        .vs-table--search-input {
          padding: 0.9rem 2.5rem;
          font-size: 1rem;

          & + i {
            left: 1rem;
          }

          &:focus + i {
            left: 1rem;
          }
        }
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, .05);

        td {
          padding: 10px;
          text-align: center;

          &:first-child {
            border-top-left-radius: .5rem;
            border-bottom-left-radius: .5rem;
          }

          &:last-child {
            border-top-right-radius: .5rem;
            border-bottom-right-radius: .5rem;
          }

          &.img-container {
            span {
              display: flex;
              justify-content: flex-start;
            }

            .product-img {
              height: 110px;
            }
          }
        }

        td.td-check {
          padding: 20px !important;
        }

        td.td.vs-table--td.actionTD > span {
          display: flex;
          justify-content: center;
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
          justify-content: center !important;
        }
      }

      th.td-check {
        padding: 0 15px !important;
      }

      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }

  .vs-popup {
    max-width: 900px;
  }

  .empty-state {
    padding: 32px 16px;
    text-align: center;
    color: #6b7280;
  }

  .empty-state .title {
    font-weight: 600;
    color: #111827;
  }

  .empty-state .subtitle {
    margin-top: 4px;
    font-size: 13px;
  }
}
</style>
